import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'sonner';
import './App.css';

// Import components
import AuthPage from './components/AuthPage';
import LandingPage from './components/LandingPage';
import Dashboard from './components/Dashboard';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import AssessmentFlow from './components/AssessmentFlow';
import EmployeeManagement from './components/EmployeeManagement';
import AssessmentManagement from './components/AssessmentManagement';
import Reports from './components/Reports';
import AdminPanel from './components/AdminPanel';
import AssessmentConfigPanel from './components/AssessmentConfigPanel';
import EnneagramAssessment from './components/EnneagramAssessment';
import AdminDashboard from './components/AdminDashboard';
import CompanySubscriptionView from './components/CompanySubscriptionView';
import ActionPlanDetail from './components/ActionPlanDetail';
import MyTeamDashboard from './components/MyTeamDashboard';
import OrganizationalChart from './components/OrganizationalChart';
import EmployeeDashboard from './components/EmployeeDashboard';
import NOM035Guide3Page from './components/NOM035Guide3Page';
import MagicLinkFlow from './components/MagicLinkFlow';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { ViewProvider, useView } from './contexts/ViewContext';

import ConsentScreen from './components/ConsentScreen';
import PrivacyNoticePage from './components/PrivacyNoticePage';
import CycleDecisionScreen from './components/CycleDecisionScreen';
import JobProfilesManager from './components/JobProfilesManager';
import PulseSurveyResponse from './components/PulseSurveyResponse';

// Determine API base URL:
// - In preview/localhost: use REACT_APP_BACKEND_URL  
// - In production with custom domain (e.g. mevia.mx): use relative '/api' to avoid CORS
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const isCustomDomain = typeof window !== 'undefined' 
  && !window.location.hostname.includes('localhost') 
  && !window.location.hostname.includes('preview.emergentagent.com');
const API = isCustomDomain ? '/api' : (BACKEND_URL ? `${BACKEND_URL}/api` : '/api');

import { getToken, clearAuth } from './utils/tokenStorage';
import logger from './utils/logger';

// Configure axios defaults
axios.defaults.baseURL = API;
axios.defaults.timeout = 30000;
axios.defaults.withCredentials = true; // SEC-001: Send httpOnly cookies with every request
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.headers.common['Accept'] = 'application/json';

// Axios interceptor for auth — cookies are sent automatically
import ErrorBoundary from './components/ErrorBoundary';
import { isInQuestionnaireMode } from './utils/questionnaireMode';

axios.interceptors.request.use((config) => {
  return config;
}, (error) => {
  logger.error('Request interceptor error:', error);
  return Promise.reject(error);
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    logger.error('Response error:', error);
    if (error.response?.status === 401) {
      if (isInQuestionnaireMode()) {
        logger.warn('401 during questionnaire — suppressing redirect, progress is autosaved');
        return Promise.reject(error);
      }
      clearAuth();
      window.location.href = '/auth';
    }
    return Promise.reject(error);
  }
);

// Protected Route Component with consent check and D9 cycle decision
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const [consentChecked, setConsentChecked] = useState(false);
  const [requiresConsent, setRequiresConsent] = useState(false);
  const [cycleDecisionData, setCycleDecisionData] = useState(null);
  const [cycleChecked, setCycleChecked] = useState(false);
  
  useEffect(() => {
    if (user && !loading) {
      // Check consent requirement
      const checkConsent = async () => {
        try {
          const response = await axios.get('/consent/check');
          if (response.data?.success && response.data.data?.requires_consent) {
            setRequiresConsent(true);
          }
        } catch {
          // If check fails, don't block - allow access
        } finally {
          setConsentChecked(true);
        }
      };
      checkConsent();
      
      // D9: Check cycle expiration for company_admin
      const checkCycleStatus = async () => {
        if (user.rol === 'company_admin' && user.empresa_id) {
          try {
            const res = await axios.get(`/v5/ciclos/${user.empresa_id}/cycle-status`);
            if (res.data?.success && res.data.data?.requires_decision) {
              setCycleDecisionData(res.data.data);
            }
          } catch {
            // Don't block on error
          }
        }
        setCycleChecked(true);
      };
      checkCycleStatus();
    }
  }, [user, loading]);
  
  if (loading || (user && !consentChecked) || (user && !cycleChecked)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/auth" replace />;
  
  if (requiresConsent) {
    return <ConsentScreen onConsentGiven={() => setRequiresConsent(false)} />;
  }
  
  // D9: Show cycle decision screen for company_admin with expired cycle
  if (cycleDecisionData) {
    return (
      <CycleDecisionScreen
        cycleData={cycleDecisionData}
        onDecisionMade={() => {
          setCycleDecisionData(null);
        }}
      />
    );
  }
  
  return children;
}

// Public Route Component (redirect if authenticated)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return user ? <Navigate to="/dashboard" replace /> : children;
}

// M-03: Dashboard router that respects ViewContext
function DashboardRouter() {
  const { user } = useAuth();
  const { isEmployeeView } = useView();
  
  if (user?.rol === 'super_admin') return <SuperAdminDashboard />;
  
  // M-03 §3.3: Admin can switch to employee view
  if (isEmployeeView) return <EmployeeDashboard />;
  
  if (user?.rol === 'company_admin') return <AdminDashboard />;
  if (user?.rol === 'employee') return <EmployeeDashboard />;
  return <Dashboard />;
}

function AppContent() {
  const { user } = useAuth();
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Router>
        <Routes>
          {/* Public routes */}
          <Route path="/privacidad" element={<PrivacyNoticePage />} />
          <Route path="/privacy" element={<PrivacyNoticePage />} />
          {/* M-05: Magic Link public route */}
          <Route path="/nom035/magic/:token" element={<MagicLinkFlow />} />
          <Route
            path="/auth"
            element={
              <PublicRoute>
                <AuthPage />
              </PublicRoute>
            }
          />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <AuthPage />
              </PublicRoute>
            }
          />
          
          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardRouter />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminPanel />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/assessment/:assessmentId"
            element={
              <ProtectedRoute>
                <AssessmentFlow />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/employees"
            element={
              <ProtectedRoute>
                <EmployeeManagement />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/assessments-management"
            element={
              <ProtectedRoute>
                <AssessmentManagement />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/reports/employee/:employeeId"
            element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/assessment-config"
            element={
              <ProtectedRoute>
                <AssessmentConfigPanel />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/enneagram"
            element={
              <ProtectedRoute>
                <EnneagramAssessment />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/super-admin"
            element={
              <ProtectedRoute>
                <SuperAdminDashboard />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/my-subscription"
            element={
              <ProtectedRoute>
                <CompanySubscriptionView />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/action-plans/:planId"
            element={
              <ProtectedRoute>
                <ActionPlanDetail />
              </ProtectedRoute>
            }
          />
          
          {/* Action Plans list redirects to Dashboard with tab param */}
          <Route
            path="/action-plans"
            element={
              <ProtectedRoute>
                <Navigate to="/dashboard?tab=action_plans" replace />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/my-team"
            element={
              <ProtectedRoute>
                <MyTeamDashboard />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/org-chart"
            element={
              <ProtectedRoute>
                <OrganizationalChart />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/job-profiles"
            element={
              <ProtectedRoute>
                <JobProfilesManager />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/nom035/guide3"
            element={
              <ProtectedRoute>
                <NOM035Guide3Page />
              </ProtectedRoute>
            }
          />

          <Route
            path="/pulse-survey/:surveyId"
            element={
              <ProtectedRoute>
                <PulseSurveyResponse />
              </ProtectedRoute>
            }
          />
          
          {/* Default redirect */}
          <Route path="/" element={<LandingPage />} />
        </Routes>
      </Router>
      
      <Toaster position="top-right" />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <SubscriptionProvider>
          <ViewProvider>
            <ErrorBoundary>
              <AppContent />
            </ErrorBoundary>
          </ViewProvider>
        </SubscriptionProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;